import { useState, useEffect, useCallback } from 'react';
import { pushNotificationService } from '../services/pushNotificationService';

export interface UsePushNotificationsReturn {
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  requestPermission: () => Promise<void>;
}

/**
 * Хук для работы с push-уведомлениями
 */
export function usePushNotifications(): UsePushNotificationsReturn {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Проверяем статус разрешения и подписки при монтировании
  useEffect(() => {
    const checkStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const currentPermission = pushNotificationService.getPermissionStatus();
        setPermission(currentPermission);

        if (currentPermission === 'granted') {
          const subscribed = await pushNotificationService.isSubscribed();
          setIsSubscribed(subscribed);
        } else {
          setIsSubscribed(false);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error checking push notification status:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, []);

  // Запрос разрешения
  const requestPermission = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const newPermission = await pushNotificationService.requestPermission();
      setPermission(newPermission);

      if (newPermission !== 'granted') {
        setError('Notification permission was denied');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error requesting notification permission:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Подписка на push-уведомления
  const subscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const subscription = await pushNotificationService.subscribe();
      if (subscription) {
        setIsSubscribed(true);
        setPermission('granted');
      } else {
        setError('Failed to subscribe to push notifications');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error subscribing to push notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Отписка от push-уведомлений
  const unsubscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const success = await pushNotificationService.unsubscribe();
      if (success) {
        setIsSubscribed(false);
      } else {
        setError('Failed to unsubscribe from push notifications');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error unsubscribing from push notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    requestPermission,
  };
}

